import Card from "components/Card/Card";
import LoadingIcon from "components/Loading/LoadingIcon";
import { memo } from "react";
import { useGetNotesQuery } from "services/notes-api";
import type { NoteType } from "types/DataTypes";

import NoteItem from "./NoteItem";
import NotesForm from "./NotesForm";

import type { NotesEntity } from "./notesAdapter";

type NotesListProps = {
  entity: NotesEntity;
  heading?: string;
  cardClassNames?: string;
  onEditNote?: (note: NoteType) => void;
  editingNote?: NoteType | null;
  onEditComplete?: () => void;
};

const NotesList = ({
  entity,
  heading = "Notes",
  cardClassNames,
  onEditNote,
  editingNote,
  onEditComplete,
}: NotesListProps) => {
  // build query parameters based on entity context
  // for lien files, query by file + service to get BOTH new (lien_file) and legacy (lien) notes
  // we'll filter client-side to show only the relevant ones
  const queryParams = {
    ...(entity.fileId && { file: entity.fileId }),
    page_size: 50,
    ...(entity.claimId && { claim: entity.claimId }),
    // for lien file context: query by service to get all notes (legacy and new)
    // for non-lien contexts: also query by service
    ...(entity.serviceId && { service: entity.serviceId }),
  };

  // skip query if we don't have either a fileId or claimId
  const shouldSkipQuery = !entity.fileId && !entity.claimId;

  const {
    data: notesData,
    isLoading,
    error,
  } = useGetNotesQuery(queryParams, { skip: shouldSkipQuery });

  // for claims, we use server-side filtering, so no client-side filtering needed
  // for lien files, we show both new (lien_file) and legacy (lien) notes
  // for other contexts, we still need client-side filtering for hierarchical visibility
  const notes = entity.claimId
    ? notesData?.results || []
    : (notesData?.results || []).filter((note) => {
        // if we're in a lien file context, show notes with matching lien_file OR matching service (for legacy)
        if (entity.lienFileId) {
          return (
            note.lien_file === entity.lienFileId ||
            (note.service === entity.serviceId && note.lien && !note.lien_file)
          );
        }

        // if we're in a service context, only show service-specific notes (but not lien notes)
        if (entity.serviceId) {
          return (
            note.service === entity.serviceId && !note.lien && !note.lien_file
          );
        }

        // if we're in a file-only context, only show file-only notes (no service, no lien, no lien_file, no claim)
        return !note.service && !note.lien && !note.lien_file && !note.claim;
      });

  return (
    <Card heading={heading} classNames={cardClassNames}>
      {/* create/edit form at the top */}
      <div className="Notes__form-container">
        <NotesForm
          entity={entity}
          editingNote={editingNote}
          onEditComplete={onEditComplete}
        />
      </div>

      {isLoading && <LoadingIcon />}

      {error && (
        <div className="Notes__error">
          <p>Failed to load notes. Please try again.</p>
        </div>
      )}

      {!isLoading && !error && notes.length === 0 && (
        <div className="Notes__empty">
          <p>No notes found.</p>
        </div>
      )}

      {!isLoading && notes.length > 0 && (
        <div className="Notes__items">
          {notes.map((note) => (
            <NoteItem key={note.id} note={note} onEdit={onEditNote} />
          ))}
        </div>
      )}
    </Card>
  );
};

export default memo(NotesList);
